import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["enableToggle", "builderContainer", "itemsList", "item", "destroyField"]
  static values = {
    modelName: { type: String, default: "checklist_template" },
    customFields: { type: Array, default: [] }
  }

  connect() {
    this.itemCount = this.itemTargets.length

    // Auto-detect model name from existing form fields if not explicitly set
    if (!this.hasModelNameValue) {
      const firstInput = this.element.querySelector('input[name*="[checklist_items_attributes]"]')
      if (firstInput) {
        const match = firstInput.name.match(/^(\w+)\[/)
        if (match) {
          this.modelNameValue = match[1]
        }
      }
    }
  }

  toggleBuilder() {
    const enabled = this.enableToggleTarget.checked
    this.builderContainerTarget.classList.toggle("hidden", !enabled)
  }

  addItem() {
    const timestamp = new Date().getTime()
    const newItemHtml = this.createItemTemplate(timestamp)

    this.itemsListTarget.insertAdjacentHTML('beforeend', newItemHtml)
    this.itemCount++
    this.updatePositions()
  }

  removeItem(event) {
    const item = event.target.closest('[data-checklist-builder-target="item"]')

    // If item is persisted (has an ID), mark for destruction instead of removing
    const destroyField = item.querySelector('[data-checklist-builder-target="destroyField"]')
    if (destroyField && destroyField.previousElementSibling?.value) {
      destroyField.value = "1"
      item.style.display = "none"
    } else {
      item.remove()
    }

    this.updatePositions()
  }

  updateItemType(event) {
    const select = event.target
    const item = select.closest('[data-checklist-builder-target="item"]')
    const dropdownOptions = item.querySelector('[data-checklist-builder-target="dropdownOptions"]')

    if (dropdownOptions) {
      dropdownOptions.classList.toggle('hidden', select.value !== 'dropdown')
    }
  }

  updatePositions() {
    this.itemTargets.forEach((item, index) => {
      const positionField = item.querySelector('input[name*="[position]"]')
      if (positionField) {
        positionField.value = index
      }
    })
  }

  // Mirrors app/views/shared/_custom_fields_form.html.erb so newly-added checklist
  // items (added client-side, before the form is ever submitted/reloaded) also show
  // any admin-defined custom fields for the checklist_item domain.
  customFieldsHtml(namePrefix, uid) {
    if (!this.customFieldsValue || this.customFieldsValue.length === 0) return ""

    const fields = this.customFieldsValue.map((definition) => {
      const fieldId = `${namePrefix.replace(/[\[\]]/g, "_")}_custom_field_${definition.id}_${uid}`
      const fieldName = `${namePrefix}[extra_values][custom_fields][${definition.id}]`
      const requiredAttr = definition.required ? "required" : ""
      const labelHtml = `<label for="${fieldId}" class="block text-gray-300 text-xs font-medium mb-1">${definition.label}${definition.required ? ' <span class="text-red-400">*</span>' : ""}</label>`
      const inputClass = "w-full px-3 py-2 bg-[#0f1b23] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary text-sm"

      let controlHtml
      switch (definition.field_type) {
        case "number":
          controlHtml = `<input type="number" step="any" id="${fieldId}" name="${fieldName}" ${requiredAttr} class="${inputClass}">`
          break
        case "date":
          controlHtml = `<input type="date" id="${fieldId}" name="${fieldName}" ${requiredAttr} class="${inputClass}">`
          break
        case "select": {
          const opts = (definition.options || []).map((o) => `<option value="${o}">${o}</option>`).join("")
          controlHtml = `<select id="${fieldId}" name="${fieldName}" ${requiredAttr} class="${inputClass}"><option value=""></option>${opts}</select>`
          break
        }
        case "checkbox":
          return `
            <div class="mt-1">
              <label for="${fieldId}" class="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="hidden" name="${fieldName}" value="0">
                <input type="checkbox" id="${fieldId}" name="${fieldName}" value="1" class="w-4 h-4 text-primary bg-[#0f1b23] border-gray-600 rounded focus:ring-primary">
                ${definition.label}
              </label>
            </div>`
        default:
          controlHtml = `<input type="text" id="${fieldId}" name="${fieldName}" ${requiredAttr} class="${inputClass}">`
      }

      return `<div class="mt-1">${labelHtml}${controlHtml}</div>`
    }).join("")

    return `<div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">${fields}</div>`
  }

  escapeAttr(str) {
    return String(str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  createItemTemplate(timestamp) {
    const modelName = this.modelNameValue
    return `
      <div class="bg-[#1C2A38] border border-gray-700 rounded-lg p-4" data-checklist-builder-target="item">
        <div class="flex items-start gap-3">
          <!-- Drag Handle -->
          <div class="mt-2 cursor-move text-gray-500 hover:text-gray-300">
            <span class="material-symbols-outlined">drag_indicator</span>
          </div>

          <!-- Question Content -->
          <div class="flex-1 space-y-3">
            <!-- Question Text -->
            <div>
              <input type="text"
                     name="${modelName}[checklist_items_attributes][${timestamp}][question_text]"
                     placeholder="${this.escapeAttr(t("javascript.controllers.checklist_builder_controller.question_placeholder"))}"
                     class="w-full px-3 py-2 bg-[#0f1b23] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary">
            </div>

            <!-- Question Type -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <select name="${modelName}[checklist_items_attributes][${timestamp}][question_type]"
                        class="w-full px-3 py-2 bg-[#0f1b23] border border-gray-600 rounded-lg text-white focus:border-primary focus:ring-1 focus:ring-primary"
                        data-action="change->checklist-builder#updateItemType">
                  <option value="pass_fail">${t("javascript.controllers.checklist_builder_controller.question_types.pass_fail")}</option>
                  <option value="checkbox">${t("javascript.controllers.checklist_builder_controller.question_types.checkbox")}</option>
                  <option value="text">${t("javascript.controllers.checklist_builder_controller.question_types.text_input")}</option>
                  <option value="number">${t("javascript.controllers.checklist_builder_controller.question_types.number")}</option>
                  <option value="dropdown">${t("javascript.controllers.checklist_builder_controller.question_types.dropdown")}</option>
                </select>
              </div>

              <div class="hidden" data-checklist-builder-target="dropdownOptions">
                <input type="text"
                       name="${modelName}[checklist_items_attributes][${timestamp}][options]"
                       placeholder="${this.escapeAttr(t("javascript.controllers.checklist_builder_controller.options_placeholder"))}"
                       class="w-full px-3 py-2 bg-[#0f1b23] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary">
                <p class="text-xs text-gray-500 mt-1">${t("javascript.controllers.checklist_builder_controller.separate_options_with_commas")}</p>
              </div>
            </div>

            <!-- Checkboxes Row -->
            <div class="flex flex-wrap gap-4">
              <label class="flex items-center gap-2 text-sm text-gray-300">
                <input type="hidden" name="${modelName}[checklist_items_attributes][${timestamp}][required]" value="0">
                <input type="checkbox"
                       name="${modelName}[checklist_items_attributes][${timestamp}][required]"
                       value="1"
                       checked
                       class="w-4 h-4 text-primary bg-[#0f1b23] border-gray-600 rounded focus:ring-primary">
                ${t("javascript.controllers.checklist_builder_controller.required")}
              </label>

              <label class="flex items-center gap-2 text-sm text-gray-300">
                <input type="hidden" name="${modelName}[checklist_items_attributes][${timestamp}][requires_photo_on_failure]" value="0">
                <input type="checkbox"
                       name="${modelName}[checklist_items_attributes][${timestamp}][requires_photo_on_failure]"
                       value="1"
                       class="w-4 h-4 text-primary bg-[#0f1b23] border-gray-600 rounded focus:ring-primary">
                ${t("javascript.controllers.checklist_builder_controller.require_photo_on_failure")}
              </label>
            </div>

            <!-- Help Text (Optional) -->
            <details class="group">
              <summary class="text-sm text-gray-400 hover:text-gray-300 cursor-pointer">
                ${t("javascript.controllers.checklist_builder_controller.add_help_text")}
              </summary>
              <div class="mt-2">
                <textarea name="${modelName}[checklist_items_attributes][${timestamp}][help_text]"
                         rows="2"
                         placeholder="${this.escapeAttr(t("javascript.controllers.checklist_builder_controller.help_text_placeholder"))}"
                         class="w-full px-3 py-2 bg-[#0f1b23] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-primary focus:ring-1 focus:ring-primary text-sm"></textarea>
              </div>
            </details>

            ${this.customFieldsHtml(`${modelName}[checklist_items_attributes][${timestamp}]`, timestamp)}

            <!-- Hidden Position Field -->
            <input type="hidden" name="${modelName}[checklist_items_attributes][${timestamp}][position]" value="${this.itemCount}">
          </div>

          <!-- Remove Button -->
          <button type="button"
                  data-action="click->checklist-builder#removeItem"
                  class="mt-2 text-red-400 hover:text-red-300 transition-colors">
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
    `
  }
}
